import React from 'react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { riskReturnData, peerCountries } from '../../../data/peerData';
import { ScatterChart, Scatter, XAxis, YAxis, ZAxis, ResponsiveContainer, Tooltip, Cell, ReferenceLine, ReferenceArea } from 'recharts';
import { usePanelLink } from '../../../hooks/usePanelLink';

// Central Asia & Silk Road Corridor colors
const countryColors: Record<string, string> = {
  UZB: '#00d26a',  // Primary - Green
  KAZ: '#fbbf24',  // Kazakhstan - Amber
  KGZ: '#00a3ff',  // Kyrgyzstan - Blue
  TJK: '#00d4d4',  // Tajikistan - Cyan
  TKM: '#f97316',  // Turkmenistan - Orange
  AZE: '#ef4444',  // Azerbaijan - Red
  GEO: '#a855f7',  // Georgia - Purple
  MNG: '#6b7280'   // Mongolia - Gray
};

const CustomTooltip: React.FC<any> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const country = peerCountries.find(c => c.code === data.country);
    return (
      <div style={{
        backgroundColor: '#0a0a0f',
        border: '1px solid #00d26a',
        borderRadius: '6px',
        fontSize: '11px',
        padding: '10px 14px',
        boxShadow: '0 4px 20px rgba(0, 210, 106, 0.15)',
      }}>
        <div style={{ color: countryColors[data.country], fontWeight: 'bold', marginBottom: '4px' }}>
          {data.name} ({data.country})
        </div>
        <div className="text-gray-300 text-xs space-y-1">
          <div>Growth Potential: <span className="text-white font-medium">{data.return}%</span></div>
          <div>Risk Score: <span className="text-white font-medium">{data.risk}</span></div>
          <div>FDI Inflows: <span className="text-white font-medium">${country?.fdiInflows}B</span></div>
          <div>Signal: <span className={`font-medium ${data.signal === 'STRONG BUY' ? 'text-data-green' : data.signal === 'BUY' ? 'text-data-blue' : 'text-data-amber'}`}>{data.signal}</span></div>
        </div>
      </div>
    );
  }
  return null;
};

interface RiskReturnPanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

export const RiskReturnPanel: React.FC<RiskReturnPanelProps> = ({ isMinimized, onMinimize }) => {
  const { selectedEntity, isLinked } = usePanelLink('risk-return');

  // Get linked country for enhanced styling
  const linkedCountry = isLinked && selectedEntity?.type === 'country' ? selectedEntity.id : null;

  // Calculate risk-adjusted return for each country
  const sortedByRiskAdj = [...riskReturnData].sort((a, b) =>
    (b.return / (b.risk + 1)) - (a.return / (a.risk + 1))
  );

  return (
    <Panel id="risk-return" title="RISK-RETURN ANALYSIS" subtitle="Opportunity vs Risk Matrix" status="live" isMinimized={isMinimized} onMinimize={onMinimize}>
      <div className="flex flex-col h-full">
        {/* Chart */}
        <div className="flex-1 min-h-0 relative">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
              {/* Optimal Zone - Low Risk, High Return */}
              <ReferenceArea
                x1={0}
                x2={35}
                y1={75}
                y2={100}
                fill="#00d26a"
                fillOpacity={0.08}
                stroke="#00d26a"
                strokeOpacity={0.2}
                strokeDasharray="3 3"
              />
              {/* Danger Zone - High Risk, Low Return */}
              <ReferenceArea
                x1={55}
                x2={80}
                y1={30}
                y2={60}
                fill="#ef4444"
                fillOpacity={0.08}
                stroke="#ef4444"
                strokeOpacity={0.2}
                strokeDasharray="3 3"
              />
              <XAxis
                type="number"
                dataKey="risk"
                name="Risk"
                domain={[15, 75]}
                tick={{ fontSize: 9, fill: '#6b7280' }}
                axisLine={{ stroke: '#1e1e26' }}
                tickLine={false}
                label={{ value: 'RISK →', position: 'bottom', fontSize: 8, fill: '#6b7280', offset: 0 }}
              />
              <YAxis
                type="number"
                dataKey="return"
                name="Return"
                domain={[30, 95]}
                tick={{ fontSize: 9, fill: '#6b7280' }}
                axisLine={{ stroke: '#1e1e26' }}
                tickLine={false}
                width={35}
                label={{ value: 'GROWTH ↑', angle: -90, position: 'insideLeft', fontSize: 8, fill: '#6b7280' }}
              />
              <ZAxis type="number" dataKey="size" range={[80, 300]} />
              <ReferenceLine x={40} stroke="#374151" strokeDasharray="3 3" />
              <ReferenceLine y={65} stroke="#374151" strokeDasharray="3 3" />
              <Tooltip content={<CustomTooltip />} />
              <Scatter name="Countries" data={riskReturnData}>
                {riskReturnData.map((entry, index) => {
                  const isLinkedCountry = entry.country === linkedCountry;
                  const isUzb = entry.country === 'UZB';
                  return (
                    <Cell
                      key={`cell-${index}`}
                      fill={countryColors[entry.country]}
                      fillOpacity={linkedCountry && !isLinkedCountry ? 0.3 : 1}
                      stroke={isLinkedCountry ? '#ffffff' : isUzb ? '#00d26a' : 'transparent'}
                      strokeWidth={isLinkedCountry ? 4 : isUzb ? 3 : 0}
                    />
                  );
                })}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>

          {/* Zone Labels */}
          <div className="absolute top-6 left-8 text-xs text-data-green/70 font-medium">
            OPTIMAL ZONE
          </div>
          <div className="absolute bottom-10 right-8 text-xs text-data-red/70 font-medium">
            DANGER ZONE
          </div>

          {/* Country Labels on Chart */}
          {riskReturnData.map(d => {
            const isLinkedCountry = d.country === linkedCountry;
            return (
              <div
                key={d.country}
                className={`absolute text-xs font-medium pointer-events-none transition-all ${
                  isLinkedCountry ? 'text-xs font-bold' : ''
                }`}
                style={{
                  color: countryColors[d.country],
                  left: `${((d.risk - 15) / 60) * 85 + 8}%`,
                  top: `${100 - ((d.return - 30) / 65) * 75 - 10}%`,
                  transform: 'translate(-50%, -100%)',
                  opacity: linkedCountry && !isLinkedCountry ? 0.4 : 1,
                  textShadow: isLinkedCountry ? '0 0 4px rgba(255,255,255,0.5)' : 'none',
                }}
              >
                {d.country}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="pt-2 border-t border-terminal-border flex items-center justify-between">
          <DataSourceBadge source={DATA_SOURCES.NATEX} showLabel size="xs" />
          <span className="text-xs text-gray-500">Dec 2025</span>
        </div>
      </div>
    </Panel>
  );
};

export default RiskReturnPanel;
